import Backbone from 'backbone';
import ReactDOM from 'react-dom';

import * as metrics from 'metrics/Metrics';
import {RingLayout} from 'molbio/maps/circular/CircularLayout';
import {RingComponentOptions} from 'molbio/maps/circular/RingComponent';
import {RingView} from 'molbio/maps/circular/RingView';
import {CircularMapState} from 'molbio/maps/MapStates';

export interface BaseCircularReactWrapperViewOptions<
  TLayout extends RingLayout,
> extends RingComponentOptions<TLayout> {
  /**
   * Any custom Backbone event listeners that this view needs to set up to call `update`. By default, RingView
   * listens to the 'visible' attr of the layout and all settings in the VisibilityOption and calls `update`
   * when those change.
   */
  additionalListeners?: Array<{
    other: Backbone.Events;
    event: string;
    throttleWait?: number;
  }>;
  /**
   * Class name to be added to the SVG `g` wrapper for this view. Generally hould be the plural form of the
   * sequence feature being rendered e.g. 'annotations'
   */
  className: string;
  /**
   * Whether one of this view's event listeners getting triggered should cause the entire plasmid to re-render
   */
  redrawPlasmid: () => boolean;
  /**
   * A function to be called when the view is rendered when it is supposed to be hidden. Not the same thing as
   * 'when the view is hidden' - this is called every render cycle
   */
  onHide?: () => void;
  /** A function to be called when the view is re-rendered */
  onRedraw: () => void;
  /**
   * A function to be called when the view is rendered when it is supposed to be visible. Not the same thing
   * as 'when the view is unhidden' - this is called every render cycle
   */
  onShow?: () => void;
}

/**
 * A component that helps render a React component as part of a RingView.
 */
export default abstract class BaseCircularReactWrapperView<
  TLayout extends RingLayout,
> extends RingView<TLayout> {
  additionalListeners!: Array<{
    other: Backbone.Events;
    event: string;
    throttleWait?: number;
  }>;
  override className!: string;
  override redrawPlasmid!: () => boolean;
  onHide?: () => void;
  onRedraw!: () => void;
  onShow?: () => void;

  override initialize(options: BaseCircularReactWrapperViewOptions<TLayout>): void {
    super.initialize(options);
    this.additionalListeners = options.additionalListeners ?? [];
    this.className = options.className;
    this.onHide = options.onHide;
    this.onRedraw = options.onRedraw;
    this.onShow = options.onShow;
    this.redrawPlasmid = options.redrawPlasmid.bind(this);
    this.redraw.bind(this);
    metrics.count('bnch.molbio.react_plasmid_map.ring_mount_count');
  }

  override registerListeners(): void {
    super.registerListeners();

    for (const {other, event} of this.additionalListeners) {
      this.listenTo(other, event, () =>
        this.redrawPlasmid() ? this.triggerRedraw.bind(this) : this.update.bind(this)
      );
    }
  }

  shouldShow(): boolean {
    return this.model.get('sequence').length() > 0 && this.layout.get('visible');
  }

  override triggerRedraw(): void {
    this.onRedraw();
    super.triggerRedraw();
  }

  override redraw(): void {
    this.trigger('redraw');
  }

  /**
   * Overridden in subclasses to enable different sets of arguments for rendering fixed-thickness and
   * variable-thickness (i.e., non-segmented and segmented) rings in React
   */
  abstract _renderComponentWrapper(): void;

  renderComponentWrapper(): void {
    if (this.shouldShow()) {
      this.onShow?.();
      this.$el.show();
      this._renderComponentWrapper();
    } else {
      this.onHide?.();
      this.$el.hide();
    }
  }

  override update(): void {
    this.renderComponentWrapper();
  }

  override remove(): Backbone.View<CircularMapState> {
    this.die();
    return super.remove();
  }

  override die(): void {
    super.die();
    ReactDOM.unmountComponentAtNode(this.el);
  }

  // Handle clicks in React
  clickAction(e: Event, d: unknown): void {}

  // Handle clicks in React
  dblClickAction(e: Event, d: unknown): void {}
}
